import {
  ApiController,
  CategoryEndPoint,
  DiscountEndPoint,
  TopUserEndPoint,
} from 'config';
import {
  CompanyDTO,
  DiscountDTO,
  ICompany,
  IDiscountForMainPage,
  IDiscountsContent,
  ISimilarCheckDiscountResponse,
  ISimilarDiscountRequest,
  ISimilarDiscountResponse,
  IGetDiscountsForMainPage,
  IGetLatestDiscountsForMainPage,
  IGetDiscountByCategoryParams,
  GetDiscountsFilterParams,
  IDiscounts,
  DownloadDiscountsByXlsxParams,
  ITopDiscounts,
  ITopUser,
  GetTopDiscountsParams,
  PatchDiscountStatusBody,
  PatchRestoreFromArchiveBody,
  DiscountStatusComment,
} from 'interfaces';
import {
  get,
  post,
  patch,
  postWithFormData,
  deleteEntity,
  downloadFile,
} from '..';

export const getFilteredDiscounts = (params: GetDiscountsFilterParams) => {
  const parsedParams = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => !!v)
      .map(([k, v]) => [k, (v as string | number).toString()]),
  );

  return get<IDiscounts>({
    url: `${ApiController.discount}/${DiscountEndPoint.filter}`,
    params: parsedParams,
  });
};

export const getDiscountById = (id: number, userId: number) =>
  get<IDiscountsContent>({
    url: `${ApiController.discount}/${id}?userId=${userId}`,
  });

export const getDiscountInfo = (discountId: number) =>
  get({
    url: `/${ApiController.discount}/${discountId}/preview`,
  });

export const fetchRecentSearchRequest = () =>
  get({ url: `/${ApiController.recent}` });

export const postNewRecentSearch = (discountId: number) =>
  post({ url: `/${ApiController.recent}/${discountId}` });

export const postNewDiscount = (discountParams: DiscountDTO) => {
  const payload = { ...discountParams };

  if (payload.discountType === 'OTHER') {
    payload.sizeMin = 0;
    payload.sizeMax = 0;
  }

  if (payload.isArchived === undefined) {
    payload.isArchived = false;
  }
  return post<IDiscountsContent>({
    url: `${ApiController.discount}`,
    body: payload,
  });
};

export const postNewCompany = (params: CompanyDTO) =>
  post<ICompany>({
    url: `${ApiController.company}`,
    body: params,
  });

export const searchExistingDiscount = (companyName: string) => {
  const params = new URLSearchParams([['companyName', companyName]]);

  return get<ISimilarCheckDiscountResponse>({
    url: `${ApiController.discount}/${DiscountEndPoint.hr}`,
    params,
  });
};

export const getDiscountsForMainPage = (input: IGetDiscountsForMainPage) => {
  const processedInput = Object.entries(input).filter(
    ([, value]) => value !== undefined,
  );

  const params = new URLSearchParams(
    processedInput.map(([key, value]) => [key, String(value)]),
  );

  return get<IDiscountForMainPage[]>({
    url: `${ApiController.category}/${CategoryEndPoint.mainPage}`,
    params,
  });
};

export const getLatestDiscountsForMainPage = (
  params: IGetLatestDiscountsForMainPage,
) => {
  const parsedParams = new URLSearchParams(
    Object.entries(params).map(([key, value]) => [key, String(value)]),
  );

  return get<IDiscounts>({
    url: `/${ApiController.discount}/${DiscountEndPoint.dateBeginDesc}`,
    params: parsedParams,
  });
};

export const getDiscountsByCategory = (
  params: IGetDiscountByCategoryParams,
) => {
  const parsedParams = new URLSearchParams(
    Object.entries(params).map(([key, value]) => [key, String(value)]),
  );

  return get<IDiscounts>({
    url: `/${ApiController.discount}/${DiscountEndPoint.filter}`,
    params: parsedParams,
  });
};

export const getSearchDiscountsResults = (
  searchValue: string,
  locationId: number,
) => {
  const params = new URLSearchParams([
    ['searchValue', searchValue],
    ['locationId', String(locationId)],
  ]);

  return get({
    url: `/${ApiController.discount}/${DiscountEndPoint.search}`,
    params,
  });
};

export const getTopDiscounts = (params: GetTopDiscountsParams) => {
  const parsedParams = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, String(v)]),
  );
  return get<ITopDiscounts>({
    url: `${ApiController.statistics}/${TopUserEndPoint.topDiscounts}`,
    params: parsedParams,
  });
};

export const getTopUsers = () =>
  get<ITopUser[]>({
    url: `${ApiController.statistics}/${TopUserEndPoint.topUsers}`,
  });

export const uploadDiscountImage = (image: File) => {
  const body = new FormData();

  body.append('image', image);

  return postWithFormData<string | undefined>({
    url: `${ApiController.discount}/${DiscountEndPoint.image}`,
    body,
  });
};

export const updateDiscountRequest = (
  discountId: number,
  body: DiscountDTO,
) => {
  const payload = { ...body };

  if (payload.discountType === 'OTHER') {
    payload.sizeMin = 0;
    payload.sizeMax = 0;
  }

  return patch<IDiscountsContent>({
    url: `${ApiController.discount}/${discountId}`,
    body: payload,
  });
};

export const deleteDiscountById = (id: string) =>
  deleteEntity(`${ApiController.discount}/${id}`);

export const archiveDiscountById = (id: string) =>
  patch({
    url: `${ApiController.discount}/${id}/archive`,
    body: {
      editVersion: 0,
    },
  });

export const patchDiscountStatus = (
  id: string,
  body: PatchDiscountStatusBody,
) =>
  patch<IDiscountsContent>({
    url: `${ApiController.discount}/${id}/${DiscountEndPoint.status}`,
    body,
  });

export const getDiscountStatusComments = (discountId: number) =>
  get<DiscountStatusComment[]>({
    url: `${ApiController.discount}/${discountId}/${DiscountEndPoint.statusComments}`,
  });

export const patchRestoreFromArchive = (
  id: string,
  body: PatchRestoreFromArchiveBody,
) =>
  patch<IDiscountsContent>({
    url: `${ApiController.discount}/${id}/${DiscountEndPoint.restoredFromArchive}`,
    body,
  });

export const fetchSimilarDiscounts = (objParams: ISimilarDiscountRequest) => {
  const params = new URLSearchParams(
    Object.entries(objParams).map(([key, value]) => [key, String(value)]),
  );

  return get<ISimilarDiscountResponse>({
    url: `${ApiController.discount}/${DiscountEndPoint.similar}`,
    params,
  });
};

export const deleteAllRecentSearchDiscounts = () => {
  return deleteEntity(`${ApiController.recent}`);
};

export const deleteRecentSearchDiscountByID = (id: string) => {
  return deleteEntity(`${ApiController.recent}/${id}`);
};

export const downloadDiscountsByXlsx = (
  params: DownloadDiscountsByXlsxParams,
) => {
  const parsedParams = new URLSearchParams(
    Object.entries(params)
      .filter(([, value]) => value !== null)
      .map(([key, value]) => [key, String(value)]),
  );

  return downloadFile({
    url: `${ApiController.discount}/${DiscountEndPoint.download}/xlsx`,
    params: parsedParams,
    filename: 'discounts.xlsx',
  });
};

export const downloadDiscountsByIds = (discountIds: number[]) => {
  const params = new URLSearchParams([['discountIds', discountIds.toString()]]);

  return downloadFile({
    url: `${ApiController.discount}/${DiscountEndPoint.downloadByIds}`,
    params,
    filename: 'discounts.xlsx',
  });
};
